import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Space, Tour, type TourProps, type TourStepProps } from 'antd';
import { completeEduFirstRun, getEduFirstRun, getEduTours } from '@/api/edu';
import type { EduTour } from '@/api/types';
import { EDU, track, trackSessionStart } from './eduClient';
import { FIRST_RUN_SURFACE, homePathForSurface, targetGetter, toPlacement } from './tourModel';
import { TourContext, type StartTourOpts, type TourContextValue } from './TourContext';

/**
 * Tour engine (C1) + first-run flow (C2). Renders AntD `Tour` as the data-driven driver: it draws
 * whatever steps the `/v1/edu/tours` definition returns (spotlight + tooltip card + next/back/skip +
 * progress), resolving each `step.target` (a CSS selector or a `data-tour` anchor) at render time.
 * NO tour content is hard-coded here — tours are DATA.
 *
 * Accessibility (WCAG 2.2 AA): AntD Tour manages focus into the step card and closes on Esc / the X /
 * Skip (never a focus trap, always skippable); we add a polite aria-live region so each step change
 * is announced to screen readers. Reduced-motion is honoured globally (global.css).
 *
 * The first-run offer is NON-BLOCKING (a dismissible notification, never a modal) and degrades to
 * nothing when the endpoint is absent.
 */

const OFFER_KEY = 'edu-first-run-offer';
const DISMISS_KEY = 'ol.edu.firstRunDismissed';

// Module-scoped so the once-per-load first-run check survives React 18 StrictMode's dev remount.
let firstRunChecked = false;

function firstRunLocallyDismissed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}
function setFirstRunDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, 'true');
  } catch {
    /* non-fatal */
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [open, setOpen] = useState(false);
  const [activeTour, setActiveTour] = useState<EduTour | null>(null);
  const [current, setCurrent] = useState(0);
  const [liveMsg, setLiveMsg] = useState('');

  const activeTourRef = useRef<EduTour | null>(null);
  const currentRef = useRef(0);
  const isFirstRunRef = useRef(false);
  const finishedRef = useRef(false);
  const lastStepEmittedRef = useRef(-1);
  const offerResolvedRef = useRef(false);

  const cacheRef = useRef<Map<string, EduTour[]>>(new Map());
  const inflightRef = useRef<Map<string, Promise<EduTour[]>>>(new Map());

  const loadTours = useCallback(async (surface: string): Promise<EduTour[]> => {
    const cache = cacheRef.current;
    const cached = cache.get(surface);
    if (cached) return cached;
    const inflight = inflightRef.current;
    const pending = inflight.get(surface);
    if (pending) return pending;
    const p = getEduTours(surface)
      .then((tours) => {
        cache.set(surface, tours);
        inflight.delete(surface);
        return tours;
      })
      .catch(() => {
        cache.set(surface, []); // endpoint absent → cache empty so we do not re-hammer it
        inflight.delete(surface);
        return [] as EduTour[];
      });
    inflight.set(surface, p);
    return p;
  }, []);

  const startTour = useCallback(
    (tour: EduTour, opts?: StartTourOpts) => {
      if (!tour?.steps?.length) return;
      const begin = () => {
        activeTourRef.current = tour;
        currentRef.current = 0;
        isFirstRunRef.current = Boolean(opts?.isFirstRun);
        finishedRef.current = false;
        lastStepEmittedRef.current = 0;
        setActiveTour(tour);
        setCurrent(0);
        setOpen(true);
        setLiveMsg(
          `Tour started: ${tour.title}. Step 1 of ${tour.steps.length}: ${tour.steps[0].title}`,
        );
        track(EDU.tourStarted, { tourId: tour.tourId, steps: tour.steps.length });
        track(EDU.tourStepViewed, { tourId: tour.tourId, step: 0 });
      };
      if (opts?.navigate) {
        const home = homePathForSurface(tour.surface);
        if (home) {
          navigate(home);
          // Let the destination surface mount so AntD Tour can resolve its anchors on open.
          window.setTimeout(begin, 350);
          return;
        }
      }
      begin();
    },
    [navigate],
  );

  const closeOverlay = useCallback(() => {
    setOpen(false);
    setActiveTour(null);
    setLiveMsg('');
    activeTourRef.current = null;
  }, []);

  const handleChange = useCallback((next: number) => {
    currentRef.current = next;
    setCurrent(next);
    const t = activeTourRef.current;
    if (!t) return;
    const step = t.steps[next];
    setLiveMsg(`Step ${next + 1} of ${t.steps.length}: ${step?.title ?? ''}`);
    if (next !== lastStepEmittedRef.current) {
      lastStepEmittedRef.current = next;
      track(EDU.tourStepViewed, { tourId: t.tourId, step: next });
    }
  }, []);

  const handleFinish = useCallback(() => {
    finishedRef.current = true;
    const t = activeTourRef.current;
    if (t) {
      track(EDU.tourCompleted, { tourId: t.tourId, steps: t.steps.length });
      if (isFirstRunRef.current) completeEduFirstRun(t.tourId).catch(() => {});
    }
  }, []);

  const handleClose = useCallback(() => {
    if (!finishedRef.current) {
      const t = activeTourRef.current;
      if (t) track(EDU.tourSkipped, { tourId: t.tourId, atStep: currentRef.current });
    }
    finishedRef.current = false;
    closeOverlay();
  }, [closeOverlay]);

  // ---- first-run offer (non-blocking) --------------------------------------------------------
  const acceptFirstRun = useCallback(
    (tour: EduTour) => {
      if (offerResolvedRef.current) return;
      offerResolvedRef.current = true;
      notification.destroy(OFFER_KEY);
      track(EDU.firstRunAccepted, { tourId: tour.tourId });
      startTour(tour, { isFirstRun: true, navigate: true });
    },
    [notification, startTour],
  );

  const dismissFirstRun = useCallback(
    (tour: EduTour) => {
      if (offerResolvedRef.current) return;
      offerResolvedRef.current = true;
      setFirstRunDismissed();
      track(EDU.firstRunDismissed, { tourId: tour.tourId });
    },
    [],
  );

  const offerFirstRun = useCallback(
    (tour: EduTour) => {
      offerResolvedRef.current = false;
      track(EDU.firstRunOffered, { tourId: tour.tourId });
      notification.open({
        key: OFFER_KEY,
        message: 'Take the guided first run?',
        description: `${tour.title} — about 20 minutes: instantiate, configure, break, simulate, then publish your first workflow.`,
        placement: 'bottomRight',
        duration: 0,
        btn: (
          <Space>
            <Button
              size="small"
              onClick={() => {
                notification.destroy(OFFER_KEY);
                dismissFirstRun(tour);
              }}
            >
              Maybe later
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => acceptFirstRun(tour)}
            >
              Start tour
            </Button>
          </Space>
        ),
        onClose: () => dismissFirstRun(tour),
      });
    },
    [notification, acceptFirstRun, dismissFirstRun],
  );

  const relaunchFirstRun = useCallback(async () => {
    const tours = await loadTours(FIRST_RUN_SURFACE);
    if (tours.length > 0) startTour(tours[0], { isFirstRun: true, navigate: true });
  }, [loadTours, startTour]);

  useEffect(() => {
    trackSessionStart(); // idempotent (its own module guard)
    if (firstRunChecked) return;
    firstRunChecked = true;
    let cancelled = false;
    (async () => {
      try {
        const state = await getEduFirstRun();
        if (cancelled || !state.showFirstRun || firstRunLocallyDismissed()) return;
        const tours = await loadTours(FIRST_RUN_SURFACE);
        const next = tours.find((t) => !state.completedTourIds.includes(t.tourId));
        if (next && !cancelled) offerFirstRun(next);
      } catch {
        /* endpoint absent → no offer, no crash */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadTours, offerFirstRun]);

  const steps = useMemo<TourProps['steps']>(() => {
    if (!activeTour) return [];
    return activeTour.steps.map((s) => ({
      title: s.title,
      description: s.body,
      // AntD types target as (() => HTMLElement) | (() => null); a getter that may return null (→
      // AntD centers the step, no spotlight) is exactly the resilient behaviour we want. Cast at the
      // boundary rather than throw away the null path.
      target: targetGetter(s.target) as TourStepProps['target'],
      placement: toPlacement(s.placement),
    }));
  }, [activeTour]);

  const value = useMemo<TourContextValue>(
    () => ({ startTour, loadTours, relaunchFirstRun, active: open }),
    [startTour, loadTours, relaunchFirstRun, open],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Tour
        open={open}
        steps={steps}
        current={current}
        onChange={handleChange}
        onClose={handleClose}
        onFinish={handleFinish}
      />
      <div aria-live="polite" aria-atomic="true" className="ol-visually-hidden">
        {liveMsg}
      </div>
    </TourContext.Provider>
  );
}
